import { useThree } from '@react-three/fiber';
import { useEffect, useRef } from 'react';
import { Spherical, Vector3 } from 'three';

/**
 * MAP-102. Rotate, drag and zoom with inertia.
 *
 * Zoom moves toward the cursor rather than the screen centre, so the coastline
 * under the pointer stays under the pointer; and the polar angle is clamped
 * just short of the poles, where an unclamped orbit flips the world over.
 */
export interface OrbitCameraOptions {
  minDistance?: number;
  maxDistance?: number;
  damping?: number;
  /** A direction on the globe to face on arrival, so the world is not off-screen. */
  facing?: { x: number; y: number; z: number } | null;
}

export function OrbitCamera({
  minDistance = 1.25,
  maxDistance = 6,
  damping = 0.12,
  facing = null,
}: OrbitCameraOptions) {
  const { camera, gl, invalidate, size } = useThree();

  /**
   * A phone is tall and narrow, and a camera framed for a wide window puts the
   * planet's sides off both edges of it. The horizontal field of view is the
   * vertical one squeezed by the aspect ratio, so a portrait screen has to
   * stand back by exactly that much to hold the same world.
   *
   * Never less than 1: a wide screen is framed as it always was.
   */
  const fit = Math.max(1, 1 / Math.max(0.4, size.width / Math.max(1, size.height)));
  const reach = maxDistance * fit;

  const state = useRef({
    spherical: new Spherical(3, Math.PI / 2 - 0.3, 0),
    target: new Spherical(3, Math.PI / 2 - 0.3, 0),
    velocity: { theta: 0, phi: 0 },
    dragging: false,
    lastPointer: { x: 0, y: 0 },
    pointers: new Map<number, { x: number; y: number }>(),
    pinchDistance: 0,
  });

  // Face whatever the world actually occupies. Without this the first view can
  // easily be open ocean, because which landmasses a planet uses depends on its
  // regions, not on where the camera happens to start.
  //
  // It also re-aims whenever the place you are looking at changes: zooming into
  // a city that is off to one side used to slide it out of frame entirely,
  // because the camera was still pointed at wherever it happened to start.
  // Stand back on arrival by whatever the shape of the screen demands.
  const fitted = useRef(false);
  useEffect(() => {
    if (fitted.current || size.width <= 0) return;
    fitted.current = true;
    if (fit <= 1) return;
    const s = state.current;
    s.target.radius = clamp(s.target.radius * fit, minDistance, reach);
    s.spherical.radius = s.target.radius;
    invalidate();
  }, [fit, reach, minDistance, size.width, invalidate]);

  const framed = useRef<string | null>(null);
  useEffect(() => {
    if (!facing) return;
    const key = `${facing.x.toFixed(4)},${facing.y.toFixed(4)},${facing.z.toFixed(4)}`;
    if (framed.current === key) return;
    const { x, y, z } = facing;
    const length = Math.hypot(x, y, z) || 1;
    const s = state.current;
    s.target.phi = clamp(Math.acos(y / length), 0.3, Math.PI - 0.3);
    s.target.theta = Math.atan2(x / length, z / length);
    framed.current = key;
    invalidate();
  }, [facing, invalidate]);

  // Hold the same span of world in view when the canvas changes width.
  //
  // Opening the panel does not cover the globe, it narrows it — and a narrower
  // canvas sees less of the world at the same distance, so whatever was near
  // the right-hand edge simply left the screen. Clicking a building could make
  // that very building disappear. Standing back by the same ratio keeps it
  // where it was, and window resizing behaves the same way.
  const lastWidth = useRef(0);
  useEffect(() => {
    const width = size.width;
    if (width <= 0) return;
    const previous = lastWidth.current;
    lastWidth.current = width;
    if (previous <= 0 || Math.abs(previous - width) < 1) return;

    const s = state.current;
    s.target.radius = clamp((s.target.radius * previous) / width, minDistance, reach);
    invalidate();
  }, [size.width, invalidate, minDistance, reach]);

  useEffect(() => {
    const element = gl.domElement;
    const s = state.current;
    const fov = ((camera as { fov?: number }).fov ?? 40) * (Math.PI / 180);

    const apply = () => {
      const position = new Vector3().setFromSpherical(s.spherical);
      camera.position.copy(position);
      camera.lookAt(aimFrom(position, s.spherical.radius, minDistance));
      invalidate();
    };

    /**
     * How far the ground turns, in radians, for one pixel of drag.
     *
     * This is what makes the world feel like a thing rather than a dial: the
     * point you put your finger on stays under your finger, whether you are
     * out at orbit or down among the buildings. The old version turned by a
     * fixed amount per pixel, which was a crawl from far away and a lurch up
     * close.
     */
    const perPixel = (radius: number) => {
      const rect = element.getBoundingClientRect();
      const altitude = Math.max(0.05, radius - SURFACE);
      return (2 * Math.tan(fov / 2) * altitude) / Math.max(1, rect.height);
    };

    /** Near the poles a step east covers less ground, so it would race away. */
    const lean = () => Math.max(0.35, Math.sin(s.target.phi));

    const pan = (dx: number, dy: number) => {
      const step = perPixel(s.target.radius);
      const dTheta = (-dx * step) / lean();
      const dPhi = -dy * step;
      s.target.theta += dTheta;
      s.target.phi = clamp(s.target.phi + dPhi, 0.08, Math.PI - 0.08);
      // Smoothed, so the throw comes from the whole gesture rather than from
      // whatever the last event happened to be.
      s.velocity.theta = s.velocity.theta * 0.35 + dTheta * 0.65;
      s.velocity.phi = s.velocity.phi * 0.35 + dPhi * 0.65;
      invalidate();
    };

    /**
     * Zoom by a proportion of the height above the ground, and hold whatever
     * is under the pointer in place while it happens.
     *
     * Working in altitude rather than in distance from the centre is what
     * stops the last part of the descent from arriving all at once: every
     * notch covers the same fraction of what is left.
     */
    const zoomAt = (factor: number, clientX: number, clientY: number) => {
      const rect = element.getBoundingClientRect();
      const before = perPixel(s.target.radius);
      const altitude = Math.max(0.02, s.target.radius - SURFACE);
      s.target.radius = clamp(SURFACE + altitude * factor, minDistance, reach);
      const after = perPixel(s.target.radius);

      const offX = clientX - rect.left - rect.width / 2;
      const offY = clientY - rect.top - rect.height / 2;
      const shift = before - after;
      s.target.theta += (offX * shift) / lean();
      s.target.phi = clamp(s.target.phi + offY * shift, 0.08, Math.PI - 0.08);
      invalidate();
    };

    const centre = (points: Array<{ x: number; y: number }>) => ({
      x: (points[0]!.x + points[1]!.x) / 2,
      y: (points[0]!.y + points[1]!.y) / 2,
    });

    const onPointerDown = (event: PointerEvent) => {
      element.setPointerCapture(event.pointerId);
      s.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      // Touching the world stops it dead, the way putting a hand on a globe does.
      s.velocity = { theta: 0, phi: 0 };
      if (s.pointers.size === 1) {
        s.dragging = true;
        s.lastPointer = { x: event.clientX, y: event.clientY };
      } else if (s.pointers.size === 2) {
        const points = [...s.pointers.values()];
        s.pinchDistance = Math.hypot(points[0]!.x - points[1]!.x, points[0]!.y - points[1]!.y);
        s.lastPointer = centre(points);
      }
    };

    const onPointerMove = (event: PointerEvent) => {
      if (!s.pointers.has(event.pointerId)) return;
      s.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (s.pointers.size >= 2) {
        // Two fingers do both jobs at once, as they do on a map: the spread
        // zooms about the point between them, and moving them together drags.
        const points = [...s.pointers.values()].slice(0, 2);
        const distance = Math.hypot(points[0]!.x - points[1]!.x, points[0]!.y - points[1]!.y);
        const middle = centre(points);
        if (s.pinchDistance > 0 && distance > 0) {
          zoomAt(s.pinchDistance / distance, middle.x, middle.y);
          pan(middle.x - s.lastPointer.x, middle.y - s.lastPointer.y);
        }
        s.pinchDistance = distance;
        s.lastPointer = middle;
        return;
      }

      if (!s.dragging) return;
      pan(event.clientX - s.lastPointer.x, event.clientY - s.lastPointer.y);
      s.lastPointer = { x: event.clientX, y: event.clientY };
    };

    const onPointerUp = (event: PointerEvent) => {
      s.pointers.delete(event.pointerId);
      if (s.pointers.size < 2) s.pinchDistance = 0;
      if (s.pointers.size === 1) {
        // Down to one finger again: carry on dragging from where it is, rather
        // than jumping by the distance between the two.
        const [only] = [...s.pointers.values()];
        if (only) s.lastPointer = { x: only.x, y: only.y };
        s.dragging = true;
      }
      if (s.pointers.size === 0) s.dragging = false;
    };

    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      // A mouse reports lines, a trackpad pixels; without this a mouse wheel
      // barely moves and a trackpad flies.
      const pixels = event.deltaMode === 1 ? event.deltaY * 16 : event.deltaY;
      zoomAt(Math.exp(clamp(pixels, -240, 240) * 0.0022), event.clientX, event.clientY);
    };

    let frame = 0;
    let last = performance.now();
    const tick = () => {
      const now = performance.now();
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;

      // The old easing moved a fixed share of the gap each frame, so the feel
      // of the whole thing changed with the frame rate. This is the same pull,
      // expressed per second.
      const rate = -Math.log(Math.max(0.001, 1 - damping)) * 60;
      const k = 1 - Math.exp(-rate * dt);

      const moving =
        Math.abs(s.velocity.theta) > 1e-6 || Math.abs(s.velocity.phi) > 1e-6;
      if (!s.dragging && moving) {
        // A throw carries on and runs down, the way a spun globe does.
        const carry = dt * 60;
        s.target.theta += s.velocity.theta * carry;
        s.target.phi = clamp(s.target.phi + s.velocity.phi * carry, 0.08, Math.PI - 0.08);
        const decay = Math.exp(-dt * 4.2);
        s.velocity.theta *= decay;
        s.velocity.phi *= decay;
        if (Math.abs(s.velocity.theta) < 1e-6) s.velocity.theta = 0;
        if (Math.abs(s.velocity.phi) < 1e-6) s.velocity.phi = 0;
      }

      const eased =
        Math.abs(s.spherical.theta - s.target.theta) > 1e-5 ||
        Math.abs(s.spherical.phi - s.target.phi) > 1e-5 ||
        Math.abs(s.spherical.radius - s.target.radius) > 1e-5;

      if (eased) {
        s.spherical.theta += (s.target.theta - s.spherical.theta) * k;
        s.spherical.phi += (s.target.phi - s.spherical.phi) * k;
        s.spherical.radius += (s.target.radius - s.spherical.radius) * k;
        apply();
      }

      frame = requestAnimationFrame(tick);
    };

    apply();
    frame = requestAnimationFrame(tick);

    element.addEventListener('pointerdown', onPointerDown);
    element.addEventListener('pointermove', onPointerMove);
    element.addEventListener('pointerup', onPointerUp);
    element.addEventListener('pointercancel', onPointerUp);
    element.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      cancelAnimationFrame(frame);
      element.removeEventListener('pointerdown', onPointerDown);
      element.removeEventListener('pointermove', onPointerMove);
      element.removeEventListener('pointerup', onPointerUp);
      element.removeEventListener('pointercancel', onPointerUp);
      element.removeEventListener('wheel', onWheel);
    };
  }, [camera, gl, invalidate, minDistance, reach, damping]);

  return null;
}

/** The ground: everything is measured as height above this, not from the core. */
const SURFACE = 1;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * How close the camera has to be before it starts to lean, and the most it
 * will ever lean.
 */
const TILT_STARTS = 1.9;
const MAX_TILT = 0.5;

/**
 * Where the camera looks: the middle of the world from orbit, leaning toward
 * the horizon as it comes down.
 *
 * The lean depends on nothing but how close the camera is, so dragging, zooming
 * and clicking all leave the view where it was. An earlier version aimed at
 * whatever region was selected, which meant clicking a building threw the view
 * across the planet to somewhere else entirely.
 *
 * The lean is also capped by the horizon. From low altitude the horizon is only
 * a few degrees away, and leaning past it aims the camera at ground hidden
 * behind the planet's own bulge — which showed as an empty ocean.
 */
function aimFrom(position: Vector3, radius: number, minDistance: number): Vector3 {
  const closeness = clamp((TILT_STARTS - radius) / Math.max(0.001, TILT_STARTS - minDistance), 0, 1);
  if (closeness <= 0) return new Vector3(0, 0, 0);

  const surface = 1.02;
  const horizon = radius > surface ? Math.acos(surface / radius) : 0.05;
  const angle = Math.min(closeness * MAX_TILT, horizon * 0.5);

  const out = position.clone().normalize();
  let east = new Vector3(0, 1, 0).cross(out);
  if (east.lengthSq() < 1e-6) east = new Vector3(1, 0, 0).cross(out);
  const north = out.clone().cross(east.normalize()).normalize();

  // Look at the surface a little way past the point directly below, which is
  // what gives a standing view rather than a plan view.
  const target = out
    .clone()
    .multiplyScalar(Math.cos(angle))
    .add(north.multiplyScalar(Math.sin(angle)))
    .multiplyScalar(surface);
  return target;
}
